import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { AppPage, AppSection, PlanPrompt } from '../../components/app/AppPage';

/**
 * Settings hub. Every row links to the screen that actually saves that setting;
 * nothing here is stored locally, so nothing here can silently fail to save.
 */
const Settings = () => {
  const { user } = useAuth();
  const companyId = user?.primary_company_id;

  const rows = [
    {
      key: 'profile',
      title: 'Your profile',
      note: user?.email ? `Name, email and password for ${user.email}.` : 'Name, email and password.',
      label: 'Edit profile',
      to: '/profile',
    },
    {
      key: 'company',
      title: 'Company details',
      note: 'Legal name, tax ID and business address used on calculations and reports.',
      label: companyId ? 'Edit company' : 'Add a company',
      to: companyId ? `/companies/${companyId}/edit` : '/companies/new',
    },
    {
      key: 'taxcode',
      title: 'Product tax codes',
      note: 'What you sell, which decides taxability and rates in each state.',
      label: 'Set tax codes',
      to: companyId ? `/company/${companyId}/tax-codes` : '/tax/codes',
    },
    {
      key: 'team',
      title: 'Team members',
      note: 'Invite colleagues and choose who can administer the company.',
      label: 'Manage users',
      to: '/company/users',
    },
    {
      key: 'integrations',
      title: 'Connected stores',
      note: 'Shopify, QuickBooks and Stripe connections that sync your sales.',
      label: 'Manage integrations',
      to: '/platforms/integrations',
    },
    {
      key: 'billing',
      title: 'Plan and billing',
      note: 'Your plan, upgrades to Standard or Pro, invoices and payment method.',
      label: 'Open billing',
      to: '/billing',
    },
    {
      key: 'api',
      title: 'API keys and usage',
      note: 'Keys for the sales tax API and MCP server, and this month’s calls.',
      label: 'Open developer console',
      to: '/developers/console',
    },
  ];

  return (
    <AppPage
      width="narrow"
      title="Settings"
      description="Everything you can configure, and where it lives."
    >
      <AppSection title="Account and company">
        <ul className="gt-rows">
          {rows.map((r) => (
            <li key={r.key} className="gt-row">
              <div>
                <Typography variant="subtitle1" component="h3" className="gt-row-title">{r.title}</Typography>
                <Typography variant="body2" color="text.secondary">{r.note}</Typography>
              </div>
              <Button component={RouterLink} to={r.to} variant="outlined" size="small">
                {r.label}
              </Button>
            </li>
          ))}
        </ul>
      </AppSection>
      <PlanPrompt feature="Multi-state tax compliance" />
    </AppPage>
  );
};

export default Settings;
